import { useState } from "react";
import { Lock } from "lucide-react";
import { PermissionDeniedState } from "@/components/ui/system-state";
import { ActionLink, SpecimenCard, SpecimenSection } from "@/features/system-states/system-state-page";
import { cn } from "@/lib/utils";

const CONTEXTS = [
  {
    id: "transfers",
    label: "Transfers",
    resource: "Transfer · TRF-2241",
    requiredRole: "Operator or Approver",
    yourRole: "Viewer",
  },
  {
    id: "wallets",
    label: "Wallets",
    resource: "Wallet · WLT-0042",
    requiredRole: "Treasury Manager",
    yourRole: "Operator",
  },
  {
    id: "admin",
    label: "Admin console",
    resource: "Admin · Tenant settings",
    requiredRole: "Tenant Admin",
    yourRole: "Operator",
  },
  {
    id: "audit",
    label: "Audit log",
    resource: "Audit · Export",
    requiredRole: "Compliance Officer",
    yourRole: "Operator",
  },
] as const;

export default function SysPermissionDenied() {
  const [active, setActive] = useState<(typeof CONTEXTS)[number]["id"]>("transfers");
  const context = CONTEXTS.find((c) => c.id === active) ?? CONTEXTS[0];

  return (
    <>
      <SpecimenSection label="Context">
        <SpecimenCard label="Resource the user attempted to reach">
          <div role="tablist" aria-label="Resource context" className="flex flex-wrap gap-2">
            {CONTEXTS.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={c.id === active}
                onClick={() => setActive(c.id)}
                className={cn(
                  "min-h-[32px] rounded-full border px-3 text-xs font-semibold transition-colors",
                  c.id === active
                    ? "border-primary/30 bg-primary-soft text-primary"
                    : "border-line bg-surface text-muted hover:text-ink"
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </SpecimenCard>
      </SpecimenSection>

      <SpecimenSection label="Permission denied">
        <PermissionDeniedState
          title="You do not have access to this resource"
          description="Your current role does not grant permission to view this resource. This does not confirm or deny that records exist here."
          icon={<Lock aria-hidden className="size-8 text-danger" />}
          details={[
            { k: "Resource", v: context.resource },
            { k: "Required role", v: context.requiredRole },
            { k: "Your role", v: context.yourRole },
            { k: "Request id", v: "req_9c1d47" },
          ]}
          action={
            <div className="flex flex-wrap justify-center gap-3">
              <ActionLink href="/admin/users">Request elevated access</ActionLink>
            </div>
          }
          footnote="Access changes are approved by a Tenant Admin and take effect immediately after approval."
        />
      </SpecimenSection>

      <SpecimenSection label="Compact variant (inline within a page)">
        <div className="rounded-lg border border-line bg-bg p-5">
          <div className="mb-3 text-xs font-semibold text-muted">Wallet detail — restricted tab</div>
          <div className="rounded-md border border-line bg-surface p-4">
            <div className="flex items-center gap-3">
              <Lock aria-hidden className="size-5 shrink-0 text-danger" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-ink">Balances are restricted</div>
                <p className="text-xs text-muted">
                  Your role can view wallet metadata but not balances or signing keys.
                </p>
              </div>
              <ActionLink href="/admin/users" variant="secondary" className="ml-auto shrink-0">
                Request access
              </ActionLink>
            </div>
          </div>
        </div>
      </SpecimenSection>
    </>
  );
}
